import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { CalendarDays } from "lucide-react";
import type { TFunction } from "i18next";
import type {
  SeasonalEvent,
  SeasonalEventPrompt,
} from "~/integrations/firebase/collections/seasonal-events";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { useTruncated } from "~/components/truncated-text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { RegionBadge } from "~/components/seasonal-events/region-badge";
import { SeasonalEventModal } from "~/components/seasonal-events";
import {
  getLeadTimeLabel,
  getLeadTimeWeeks,
} from "~/components/seasonal-events/calendar-utils";
import { useSeasonalEventsQuery } from "~/components/seasonal-events/hooks/use-seasonal-events-query";
import { useSeasonalToday } from "~/components/seasonal-events/hooks/use-seasonal-today";
import { useAnalytics } from "~/contexts/analytics-context";
import { SEMANTIC_SEARCH_PAGE_SIZE } from "~/hooks/search/constants";
import { routePath } from "~/shared/constants";
import { getHomeSeasonalEvents, type HomeSeasonalEvent } from "./utils";

// Cards abbreviate ("in 4 wks") where the calendar spells the unit out.
const leadTimeLabel = (days: number, t: TFunction): string => {
  const leadTime = getLeadTimeLabel(days);
  switch (leadTime.unit) {
    case "days":
      return t("home:seasonalEvents.leadTime.days", { count: leadTime.count });
    case "weeks":
      return t("home:seasonalEvents.leadTime.weeks", { count: leadTime.count });
    case "today":
    default:
      return t("home:seasonalEvents.leadTime.today");
  }
};

interface EventCardProps {
  homeEvent: HomeSeasonalEvent;
  onSelect: (homeEvent: HomeSeasonalEvent) => void;
}

const EventCard = ({ homeEvent, onSelect }: EventCardProps): ReactElement => {
  const { t } = useTranslation(["home"]);
  const { ref, isTruncated } = useTruncated();
  const { event, occurrence, leadTimeDays } = homeEvent;
  const start = parseISO(occurrence.start);
  const isSpan = occurrence.start !== occurrence.end;

  const card = (
    <button
      type="button"
      onClick={() => onSelect(homeEvent)}
      className="flex h-full w-full items-stretch gap-3 rounded-xl border border-border bg-background p-3 text-left outline-none transition-[background-color,transform] duration-150 ease-out hover:bg-accent focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100"
    >
      {/* Cannot shrink below 48px: MAR is 45px at dateTileMonthAlone. */}
      <span className="flex size-12 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg bg-secondary-100">
        {isSpan ? (
          <Text as="span" variant="dateTileMonthAlone">
            {format(start, "MMM")}
          </Text>
        ) : (
          <>
            <Text as="span" variant="dateTileMonth">
              {format(start, "MMM")}
            </Text>
            <Text as="span" variant="dateTileDay" numeric="tabular">
              {format(start, "d")}
            </Text>
          </>
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <Text
          as="span"
          ref={ref}
          variant="labelSmall"
          className="block w-full truncate"
        >
          {event.display_name}
        </Text>
        <span className="mt-1 flex items-center gap-2">
          <RegionBadge region={event.region} compact />
          <Text as="span" variant="caption" className="truncate">
            {leadTimeLabel(leadTimeDays, t)}
          </Text>
        </span>
      </span>
    </button>
  );

  if (!isTruncated) {
    return card;
  }

  // Anchored to the card, not the name: the name is not focusable, so a tooltip
  // on it would never open from the keyboard.
  return (
    <Tooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-normal">
        {event.display_name}
      </TooltipContent>
    </Tooltip>
  );
};

export const UpcomingSeasonalEvents = (): ReactElement | null => {
  const { t } = useTranslation(["home"]);
  const navigate = useNavigate();
  const { gaEvent } = useAnalytics();
  const { events } = useSeasonalEventsQuery();

  const today = useSeasonalToday();
  const homeEvents = useMemo(
    () => getHomeSeasonalEvents(events, today),
    [events, today],
  );

  // Left untouched on close so the modal keeps its content through the animation.
  const [selectedEvent, setSelectedEvent] = useState<SeasonalEvent | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const eventIds = homeEvents.map(({ event }) => event.event_id).join(",");
  const eventCount = homeEvents.length;

  useEffect(() => {
    if (eventCount === 0) {
      return;
    }
    gaEvent({
      type: "seasonal_events_shown",
      payload: {
        event_ids: eventIds,
        event_count: eventCount,
        surface: "home",
      },
    });
  }, [gaEvent, eventIds, eventCount]);

  if (homeEvents.length === 0) {
    return null;
  }

  const openEvent = ({ event, leadTimeDays }: HomeSeasonalEvent): void => {
    gaEvent({
      type: "seasonal_event_chosen",
      payload: { event_id: event.event_id, surface: "home" },
    });
    gaEvent({
      type: "home_event_card_selected",
      payload: {
        event_id: event.event_id,
        lead_time_weeks: getLeadTimeWeeks(leadTimeDays),
      },
    });
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const openCalendar = (): void => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const runSearch = (prompt: SeasonalEventPrompt): void => {
    setIsModalOpen(false);
    navigate({
      to: "/books",
      search: {
        q: prompt.text.trim(),
        type: "semantic",
        pageSize: String(SEMANTIC_SEARCH_PAGE_SIZE),
      },
      state: { from: routePath("/") },
    });
  };

  return (
    <div className="w-full lg:w-2xl">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CalendarDays
            aria-hidden
            className="size-3.5 shrink-0 text-secondary-500"
          />
          <Text as="h2" variant="overline">
            {t("home:seasonalEvents.title")}
          </Text>
        </div>
        <Button
          type="button"
          variant="link"
          size="small"
          className="shrink-0 gap-0.5 px-0 text-primary"
          label={t("home:seasonalEvents.viewCalendar")}
          icon="chevron-right"
          iconPosition="right"
          onClick={openCalendar}
        />
      </div>

      <ul className="grid gap-3 sm:grid-cols-3">
        {homeEvents.map((homeEvent) => (
          <li key={homeEvent.event.event_id}>
            <EventCard homeEvent={homeEvent} onSelect={openEvent} />
          </li>
        ))}
      </ul>

      <SeasonalEventModal
        initialEvent={selectedEvent}
        events={events}
        surface="home"
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onRunSearch={runSearch}
      />
    </div>
  );
};
